import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Header } from "@/components/Header";
import { BottomNav } from "@/components/BottomNav";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import {
  Plus, Trash2, Layers, Package, Pencil, PackagePlus, Save,
} from "lucide-react";
import { formatMeters } from "@/lib/format";
import { fileUrl } from "@/lib/api";
import { ImagePicker } from "@/components/ImagePicker";
import { Link } from "react-router-dom";

export default function AdminPanel() {
  const [tab, setTab] = useState("fabrics");
  const [fabrics, setFabrics] = useState([]);
  const [products, setProducts] = useState([]);
  const [summary, setSummary] = useState(null);
  const [settings, setSettings] = useState({ low_stock_threshold: 10, critical_threshold: 5 });

  const refreshAll = async () => {
    const [{ data: f }, { data: p }, { data: s }, { data: cfg }] = await Promise.all([
      api.get("/fabrics"),
      api.get("/products"),
      api.get("/admin/summary"),
      api.get("/settings"),
    ]);
    setFabrics(f);
    setProducts(p);
    setSummary(s);
    setSettings(cfg);
  };

  useEffect(() => { refreshAll(); }, []);

  return (
    <div className="min-h-screen bg-[#FAF6EF]" data-testid="admin-panel-page">
      <Header subtitle="Admin Panel" />
      <main className="max-w-md mx-auto px-5 pt-5 pb-32 space-y-5">
        <div>
          <h1 className="font-heading text-3xl font-bold text-brand-900 tracking-tight">
            Admin
          </h1>
          <p className="text-brand-700/70 mt-1 text-sm">
            Manage fabrics, products, and thresholds.
          </p>
        </div>

        {summary && (
          <section className="grid grid-cols-3 gap-2" data-testid="admin-summary">
            <StatTile label="Fabrics" value={summary.fabric_count} testid="summary-fabrics" />
            <StatTile label="Products" value={summary.product_count} testid="summary-products" />
            <StatTile
              label="Orders"
              value={summary.order_count}
              testid="summary-orders"
              to="/history"
              sublabel="View log →"
            />
            <StatTile label="Stock" value={`${formatMeters(summary.total_stock)}m`} testid="summary-stock" to="/inventory" />
            <StatTile label="Low" value={summary.low_stock_count} tone="amber" testid="summary-low" to="/inventory" />
            <StatTile label="Critical" value={summary.critical_stock_count} tone="rose" testid="summary-crit" to="/inventory" />
          </section>
        )}

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList className="w-full grid grid-cols-3 bg-brand-100 rounded-2xl h-12 p-1" data-testid="admin-tabs">
            <TabsTrigger value="fabrics" className="rounded-xl text-sm data-[state=active]:bg-white data-[state=active]:text-brand-700 data-[state=active]:shadow-sm" data-testid="tab-fabrics">
              Fabrics
            </TabsTrigger>
            <TabsTrigger value="products" className="rounded-xl text-sm data-[state=active]:bg-white data-[state=active]:text-brand-700 data-[state=active]:shadow-sm" data-testid="tab-products">
              Products
            </TabsTrigger>
            <TabsTrigger value="settings" className="rounded-xl text-sm data-[state=active]:bg-white data-[state=active]:text-brand-700 data-[state=active]:shadow-sm" data-testid="tab-settings">
              Settings
            </TabsTrigger>
          </TabsList>

          <TabsContent value="fabrics" className="mt-5 space-y-3">
            <FabricForm onSuccess={refreshAll} />
            <FabricList fabrics={fabrics} onChange={refreshAll} />
          </TabsContent>

          <TabsContent value="products" className="mt-5 space-y-3">
            <ProductForm fabrics={fabrics} onSuccess={refreshAll} />
            <ProductList products={products} fabrics={fabrics} onChange={refreshAll} />
          </TabsContent>

          <TabsContent value="settings" className="mt-5">
            <SettingsForm settings={settings} onSaved={refreshAll} />
          </TabsContent>
        </Tabs>
      </main>
      <BottomNav />
    </div>
  );
}

const StatTile = ({ label, value, tone = "default", testid, to, sublabel }) => {
  const cls = {
    default: "bg-white border-brand-200 text-brand-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    rose: "bg-rose-50 border-rose-200 text-rose-900",
  }[tone];
  const content = (
    <>
      <p className="text-[9px] uppercase tracking-[0.18em] font-medium text-brand-700/60">{label}</p>
      <p className="font-heading text-xl font-bold tracking-tight mt-0.5">{value}</p>
      {sublabel && <p className="text-[10px] text-brand-700/70 mt-1 font-medium">{sublabel}</p>}
    </>
  );
  if (to) {
    return (
      <Link
        to={to}
        className={`rounded-2xl border ${cls} p-3 transition-all hover:shadow-sm hover:-translate-y-0.5 active:scale-95 block`}
        data-testid={testid}
      >
        {content}
      </Link>
    );
  }
  return (
    <div className={`rounded-2xl border ${cls} p-3`} data-testid={testid}>
      {content}
    </div>
  );
};

/* ---------- Fabric form & list ---------- */

const FabricForm = ({ onSuccess }) => {
  const [name, setName] = useState("");
  const [stock, setStock] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Fabric name required");
    const s = parseFloat(stock || "0");
    if (!Number.isFinite(s) || s < 0) return toast.error("Invalid stock");
    setSaving(true);
    try {
      await api.post("/fabrics", { name: name.trim(), current_stock: s, image_path: imagePath });
      toast.success("Fabric added");
      setName(""); setStock(""); setImagePath(null); setOpen(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button className="w-full h-12 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white" data-testid="add-fabric-btn">
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.8} />
          Add fabric
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">New fabric</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <ImagePicker value={imagePath} onChange={setImagePath} testid="new-fabric-image" />
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Floral Pink Fabric"
              className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid="new-fabric-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Initial stock (meters)</Label>
            <Input type="number" inputMode="decimal" min="0" step="0.1" value={stock} onChange={(e) => setStock(e.target.value)}
              placeholder="e.g. 120" className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid="new-fabric-stock" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}
              className="w-full h-12 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white" data-testid="save-fabric-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const FabricList = ({ fabrics, onChange }) => {
  if (fabrics.length === 0)
    return (
      <div className="bg-white rounded-3xl border border-brand-200 p-6 text-sm text-brand-700/70 text-center">
        No fabrics yet.
      </div>
    );
  return (
    <ul className="space-y-2" data-testid="fabric-admin-list">
      {fabrics.map((f) => (
        <FabricRow key={f.id} fabric={f} onChange={onChange} />
      ))}
    </ul>
  );
};

const FabricRow = ({ fabric, onChange }) => {
  return (
    <li
      className="bg-white rounded-2xl border border-brand-200 p-4 flex items-center gap-3"
      data-testid={`admin-fabric-${fabric.id}`}
    >
      <Thumb path={fabric.image_path} fallback={Layers} testid={`fabric-thumb-${fabric.id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-heading text-base font-semibold text-brand-900 truncate">{fabric.name}</span>
        </div>
        <p className="text-sm text-brand-700/70 mt-0.5">
          <strong className="text-brand-900">{formatMeters(fabric.current_stock)}m</strong> in stock
        </p>
      </div>
      <div className="flex gap-1 shrink-0">
        <RestockDialog fabric={fabric} onSuccess={onChange} />
        <EditFabricDialog fabric={fabric} onSuccess={onChange} />
        <DeleteFabricButton fabric={fabric} onSuccess={onChange} />
      </div>
    </li>
  );
};

const Thumb = ({ path, fallback: Fallback, testid }) => (
  <div
    className="w-12 h-12 rounded-xl bg-brand-100 overflow-hidden flex items-center justify-center shrink-0"
    data-testid={testid}
  >
    {path ? (
      <img src={fileUrl(path)} alt="" className="w-full h-full object-cover" />
    ) : (
      <Fallback className="w-5 h-5 text-brand-700/60" strokeWidth={1.6} />
    )}
  </div>
);

const RestockDialog = ({ fabric, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [meters, setMeters] = useState("");
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const m = parseFloat(meters);
    if (!Number.isFinite(m) || m <= 0) return toast.error("Enter meters > 0");
    setSaving(true);
    try {
      await api.post(`/fabrics/${fabric.id}/restock`, { meters: m, note: note || null });
      toast.success(`Added ${m}m to ${fabric.name}`);
      setMeters(""); setNote(""); setOpen(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Restock failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="w-9 h-9 rounded-xl border-brand-200" data-testid={`restock-btn-${fabric.id}`}>
          <PackagePlus className="w-4 h-4 text-brand-700" strokeWidth={1.8} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Restock {fabric.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Meters to add</Label>
            <Input type="number" inputMode="decimal" min="0" step="0.1" value={meters} onChange={(e) => setMeters(e.target.value)}
              className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid={`restock-meters-${fabric.id}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Note (optional)</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="e.g. supplier ABC, GRN-122"
              className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid={`restock-note-${fabric.id}`} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}
              className="w-full h-12 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white"
              data-testid={`restock-save-${fabric.id}`}>
              {saving ? "Adding…" : "Add to stock"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const EditFabricDialog = ({ fabric, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(fabric.name);
  const [stock, setStock] = useState(String(fabric.current_stock));
  const [imagePath, setImagePath] = useState(fabric.image_path || null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const s = parseFloat(stock);
    if (!name.trim()) return toast.error("Name required");
    if (!Number.isFinite(s) || s < 0) return toast.error("Invalid stock");
    setSaving(true);
    try {
      await api.patch(`/fabrics/${fabric.id}`, {
        name: name.trim(),
        current_stock: s,
        image_path: imagePath,
      });
      toast.success("Fabric updated");
      setOpen(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) {
          setName(fabric.name);
          setStock(String(fabric.current_stock));
          setImagePath(fabric.image_path || null);
        }
        setOpen(o);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="w-9 h-9 rounded-xl border-brand-200" data-testid={`edit-fabric-${fabric.id}`}>
          <Pencil className="w-4 h-4 text-brand-700" strokeWidth={1.8} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Edit {fabric.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <ImagePicker value={imagePath} onChange={setImagePath} testid={`edit-fabric-image-${fabric.id}`} />
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-2xl bg-brand-50 border-brand-200"
              data-testid={`edit-fabric-name-${fabric.id}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Current stock (meters)</Label>
            <Input type="number" inputMode="decimal" min="0" step="0.1" value={stock} onChange={(e) => setStock(e.target.value)}
              className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid={`edit-fabric-stock-${fabric.id}`} />
            <p className="text-[11px] text-brand-700/60">For corrections / counts. Use Restock to add supplier stock with a log entry.</p>
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}
              className="w-full h-12 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white"
              data-testid={`edit-fabric-save-${fabric.id}`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DeleteFabricButton = ({ fabric, onSuccess }) => {
  const handle = async () => {
    try {
      await api.delete(`/fabrics/${fabric.id}`);
      toast.success("Fabric deleted");
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="icon" className="w-9 h-9 rounded-xl border-brand-200 text-rose-600 hover:bg-rose-50"
          data-testid={`delete-fabric-${fabric.id}`}>
          <Trash2 className="w-4 h-4" strokeWidth={1.8} />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {fabric.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Cannot delete a fabric that products are linked to. Remove or reassign those products first.
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={handle}
            data-testid={`confirm-delete-fabric-${fabric.id}`}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/* ---------- Product form & list ---------- */

const ProductForm = ({ fabrics, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [fabricId, setFabricId] = useState("");
  const [mpu, setMpu] = useState("");
  const [imagePath, setImagePath] = useState(null);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) return toast.error("Name required");
    const m = parseFloat(mpu);
    if (!Number.isFinite(m) || m <= 0) return toast.error("Meters/unit must be > 0");
    setSaving(true);
    try {
      await api.post("/products", {
        name: name.trim(),
        fabric_id: fabricId || null,
        meters_per_unit: m,
        image_path: imagePath,
      });
      toast.success("Product added");
      setName(""); setFabricId(""); setMpu(""); setImagePath(null); setOpen(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          className="w-full h-12 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white"
          data-testid="add-product-btn"
        >
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.8} />
          Add product
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">New product</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <ImagePicker value={imagePath} onChange={setImagePath} testid="new-product-image" label="Product image" />
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Floral Pink Round Cushion"
              className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid="new-product-name" />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">
              Default fabric <span className="text-brand-700/50 normal-case tracking-normal">(optional)</span>
            </Label>
            <Select value={fabricId || "__none__"} onValueChange={(v) => setFabricId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid="new-product-fabric">
                <SelectValue placeholder="No default — pick later when ordering" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No default fabric</SelectItem>
                {fabrics.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name} · {formatMeters(f.current_stock)}m</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <p className="text-[11px] text-brand-700/60">
              Pre-selects this fabric when employees pick this product. They can still change it.
            </p>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Meters required per unit</Label>
            <Input type="number" inputMode="decimal" min="0" step="0.01" value={mpu} onChange={(e) => setMpu(e.target.value)}
              placeholder="e.g. 0.75" className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid="new-product-mpu" />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}
              className="w-full h-12 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white" data-testid="save-product-btn">
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const ProductList = ({ products, fabrics, onChange }) => {
  if (products.length === 0)
    return (
      <div className="bg-white rounded-3xl border border-brand-200 p-6 text-sm text-brand-700/70 text-center">
        No products yet.
      </div>
    );
  return (
    <ul className="space-y-2" data-testid="product-admin-list">
      {products.map((p) => (
        <ProductRow key={p.id} product={p} fabrics={fabrics} onChange={onChange} />
      ))}
    </ul>
  );
};

const ProductRow = ({ product, fabrics, onChange }) => {
  return (
    <li
      className="bg-white rounded-2xl border border-brand-200 p-4 flex items-center gap-3"
      data-testid={`admin-product-${product.id}`}
    >
      <Thumb path={product.image_path} fallback={Package} testid={`product-thumb-${product.id}`} />
      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="font-heading text-base font-semibold text-brand-900 truncate">{product.name}</span>
        </div>
        <p className="text-sm text-brand-700/70 mt-0.5 truncate">
          {product.fabric_name || <span className="italic text-brand-700/50">No default fabric</span>} · <strong className="text-brand-900">{formatMeters(product.meters_per_unit)}m</strong>/unit
        </p>
      </div>
      <div className="flex gap-1 shrink-0">
        <EditProductDialog product={product} fabrics={fabrics} onSuccess={onChange} />
        <DeleteProductButton product={product} onSuccess={onChange} />
      </div>
    </li>
  );
};

const EditProductDialog = ({ product, fabrics, onSuccess }) => {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState(product.name);
  const [fabricId, setFabricId] = useState(product.fabric_id);
  const [mpu, setMpu] = useState(String(product.meters_per_unit));
  const [imagePath, setImagePath] = useState(product.image_path || null);
  const [saving, setSaving] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    const m = parseFloat(mpu);
    if (!name.trim()) return toast.error("Name required");
    if (!Number.isFinite(m) || m <= 0) return toast.error("Meters/unit must be > 0");
    setSaving(true);
    try {
      await api.patch(`/products/${product.id}`, {
        name: name.trim(),
        fabric_id: fabricId || null,
        meters_per_unit: m,
        image_path: imagePath,
      });
      toast.success("Product updated");
      setOpen(false);
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Update failed");
    } finally { setSaving(false); }
  };
  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (o) {
          setName(product.name);
          setFabricId(product.fabric_id);
          setMpu(String(product.meters_per_unit));
          setImagePath(product.image_path || null);
        }
        setOpen(o);
      }}
    >
      <DialogTrigger asChild>
        <Button variant="outline" size="icon" className="w-9 h-9 rounded-xl border-brand-200" data-testid={`edit-product-${product.id}`}>
          <Pencil className="w-4 h-4 text-brand-700" strokeWidth={1.8} />
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className="font-heading">Edit {product.name}</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <ImagePicker value={imagePath} onChange={setImagePath} testid={`edit-product-image-${product.id}`} label="Product image" />
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Name</Label>
            <Input value={name} onChange={(e) => setName(e.target.value)} className="h-12 rounded-2xl bg-brand-50 border-brand-200"
              data-testid={`edit-product-name-${product.id}`} />
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">
              Default fabric <span className="text-brand-700/50 normal-case tracking-normal">(optional)</span>
            </Label>
            <Select value={fabricId || "__none__"} onValueChange={(v) => setFabricId(v === "__none__" ? "" : v)}>
              <SelectTrigger className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid={`edit-product-fabric-${product.id}`}>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__none__">No default fabric</SelectItem>
                {fabrics.map((f) => (
                  <SelectItem key={f.id} value={f.id}>{f.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <Label className="text-xs uppercase tracking-wider text-brand-700/70">Meters per unit</Label>
            <Input type="number" inputMode="decimal" min="0" step="0.01" value={mpu} onChange={(e) => setMpu(e.target.value)}
              className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid={`edit-product-mpu-${product.id}`} />
          </div>
          <DialogFooter>
            <Button type="submit" disabled={saving}
              className="w-full h-12 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white"
              data-testid={`edit-product-save-${product.id}`}>
              {saving ? "Saving…" : "Save"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};

const DeleteProductButton = ({ product, onSuccess }) => {
  const handle = async () => {
    try {
      await api.delete(`/products/${product.id}`);
      toast.success("Product deleted");
      onSuccess && onSuccess();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>
        <Button variant="outline" size="icon" className="w-9 h-9 rounded-xl border-brand-200 text-rose-600 hover:bg-rose-50"
          data-testid={`delete-product-${product.id}`}>
          <Trash2 className="w-4 h-4" strokeWidth={1.8} />
        </Button>
      </AlertDialogTrigger>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Delete {product.name}?</AlertDialogTitle>
          <AlertDialogDescription>
            Past orders that used this product will keep their snapshot data (this won't change history).
          </AlertDialogDescription>
        </AlertDialogHeader>
        <AlertDialogFooter>
          <AlertDialogCancel>Cancel</AlertDialogCancel>
          <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" onClick={handle}
            data-testid={`confirm-delete-product-${product.id}`}>
            Delete
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
};

/* ---------- Settings tab ---------- */

const SettingsForm = ({ settings, onSaved }) => {
  const [low, setLow] = useState(String(settings.low_stock_threshold));
  const [crit, setCrit] = useState(String(settings.critical_threshold));
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setLow(String(settings.low_stock_threshold));
    setCrit(String(settings.critical_threshold));
  }, [settings]);

  const submit = async (e) => {
    e.preventDefault();
    const l = parseFloat(low);
    const c = parseFloat(crit);
    if (!Number.isFinite(l) || l < 0 || !Number.isFinite(c) || c < 0)
      return toast.error("Thresholds must be >= 0");
    setSaving(true);
    try {
      await api.put("/settings", { low_stock_threshold: l, critical_threshold: c });
      toast.success("Settings saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-brand-200 p-5 space-y-4" data-testid="settings-form">
      <h3 className="font-heading text-lg font-semibold text-brand-900">Stock thresholds</h3>
      <p className="text-sm text-brand-700/70 -mt-2">
        Indicators on Inventory and the Employee dashboard use these values.
      </p>
      <div className="space-y-1">
        <Label className="text-xs uppercase tracking-wider text-brand-700/70">Low stock at or below (meters)</Label>
        <Input type="number" inputMode="decimal" min="0" step="0.1" value={low} onChange={(e) => setLow(e.target.value)}
          className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid="settings-low-input" />
      </div>
      <div className="space-y-1">
        <Label className="text-xs uppercase tracking-wider text-brand-700/70">Critical at or below (meters)</Label>
        <Input type="number" inputMode="decimal" min="0" step="0.1" value={crit} onChange={(e) => setCrit(e.target.value)}
          className="h-12 rounded-2xl bg-brand-50 border-brand-200" data-testid="settings-crit-input" />
      </div>
      <Button type="submit" disabled={saving}
        className="w-full h-12 rounded-2xl bg-brand-700 hover:bg-brand-900 text-white" data-testid="save-settings-btn">
        <Save className="w-4 h-4 mr-1.5" strokeWidth={1.8} />
        {saving ? "Saving…" : "Save thresholds"}
      </Button>
    </form>
  );
};
